/**
 * MilestonesView - Timeline view for milestones
 *
 * Mostra milestones come eventi (non task) con:
 * - Data grande in evidenza
 * - Countdown giorni rimanenti (es. -12 giorni)
 * - Titolo, descrizione, reminders
 * - Utenti coinvolti
 * - Task collegate
 */

import React, { useMemo, useCallback } from 'react';
import {
  Calendar,
  Clock,
  Users,
  CheckCircle2,
  Circle,
  Bell,
  Link2,
  AlertTriangle,
  Plus,
} from 'lucide-react';
import { useTranslation } from '../../hooks';
import type { Milestone, Task } from '@business-assistant/shared/domain';

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    background: '#fff',
    borderRadius: '8px',
    border: '1px solid #e5e5e5',
    overflow: 'hidden',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid #e5e5e5',
    background: '#fafafa',
  } as React.CSSProperties,
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    fontSize: '14px',
  } as React.CSSProperties,
  addBtn: {
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  } as React.CSSProperties,
  list: {
    padding: '8px 0',
  } as React.CSSProperties,
  milestoneCard: {
    display: 'flex',
    padding: '16px',
    borderBottom: '1px solid #f0f0f0',
    gap: '16px',
  } as React.CSSProperties,
  dateBlock: {
    width: '80px',
    flexShrink: 0,
    textAlign: 'center',
  } as React.CSSProperties,
  dateDay: {
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: 1,
  } as React.CSSProperties,
  dateMonth: {
    fontSize: '12px',
    color: '#666',
    textTransform: 'uppercase',
    marginTop: '4px',
  } as React.CSSProperties,
  countdown: {
    fontSize: '11px',
    fontWeight: 500,
    marginTop: '6px',
    padding: '2px 6px',
    borderRadius: '10px',
    display: 'inline-block',
  } as React.CSSProperties,
  content: {
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  } as React.CSSProperties,
  title: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#333',
  } as React.CSSProperties,
  titleDone: {
    textDecoration: 'line-through',
    color: '#999',
  } as React.CSSProperties,
  description: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '8px',
    lineHeight: 1.4,
  } as React.CSSProperties,
  metaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    fontSize: '12px',
    color: '#888',
  } as React.CSSProperties,
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  } as React.CSSProperties,
  userChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    background: '#f0f0f0',
    borderRadius: '12px',
    fontSize: '11px',
  } as React.CSSProperties,
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#888',
  } as React.CSSProperties,
};

// =============================================================================
// TYPES
// =============================================================================

export interface MilestonesViewProps {
  milestones: Milestone[];
  tasks?: Task[];
  onToggleComplete?: (id: string) => void;
  onAddMilestone?: () => void;
  onEditMilestone?: (id: string) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

function getDaysUntil(dateStr: string): number {
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

// formatCountdown and formatDate are now handled inside the component with locale awareness

// =============================================================================
// COMPONENT
// =============================================================================

export function MilestonesView({
  milestones,
  tasks = [],
  onToggleComplete,
  onAddMilestone,
  onEditMilestone,
}: MilestonesViewProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';

  // Locale-aware date formatting
  const formatDate = useCallback((dateStr: string): { day: string; month: string } => {
    const d = new Date(dateStr);
    return {
      day: d.getDate().toString(),
      month: d.toLocaleDateString(locale, { month: 'short' }),
    };
  }, [locale]);

  // Locale-aware countdown formatting
  const formatCountdown = useCallback((days: number): { text: string; color: string; bg: string } => {
    if (days < 0) {
      return {
        text: t('milestones.daysAgo', { count: Math.abs(days) }),
        color: '#dc2626',
        bg: '#fef2f2',
      };
    }
    if (days === 0) {
      return {
        text: t('common.today'),
        color: '#f59e0b',
        bg: '#fef3c7',
      };
    }
    if (days === 1) {
      return {
        text: t('common.tomorrow'),
        color: '#f59e0b',
        bg: '#fef3c7',
      };
    }
    if (days <= 7) {
      return {
        text: t('milestones.inDays', { count: days }),
        color: '#f59e0b',
        bg: '#fef3c7',
      };
    }
    return {
      text: t('milestones.inDays', { count: days }),
      color: '#22c55e',
      bg: '#dcfce7',
    };
  }, [t]);

  // Sort milestones by date
  const sortedMilestones = useMemo(() => {
    return [...milestones].sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  }, [milestones]);

  // Separate upcoming and past
  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    return sortedMilestones.reduce(
      (acc, m) => {
        if (m.completed || new Date(m.dueDate) < now) {
          acc.past.push(m);
        } else {
          acc.upcoming.push(m);
        }
        return acc;
      },
      { upcoming: [] as Milestone[], past: [] as Milestone[] }
    );
  }, [sortedMilestones]);

  const getLinkedTasks = (taskIds?: string[]) => {
    if (!taskIds?.length) return [];
    return tasks.filter((t) => taskIds.includes(t.id));
  };

  const renderMilestone = (milestone: Milestone) => {
    const { day, month } = formatDate(milestone.dueDate);
    const daysUntil = getDaysUntil(milestone.dueDate);
    const countdown = formatCountdown(daysUntil);
    const linkedTasks = getLinkedTasks(milestone.linkedTaskIds);
    const isOverdue = daysUntil < 0 && !milestone.completed;

    return (
      <div key={milestone.id} style={styles.milestoneCard}>
        {/* Date block */}
        <div style={styles.dateBlock}>
          <div
            style={{
              ...styles.dateDay,
              color: isOverdue ? '#dc2626' : milestone.completed ? '#22c55e' : '#333',
            }}
          >
            {day}
          </div>
          <div style={styles.dateMonth}>{month}</div>
          {!milestone.completed && (
            <div
              style={{
                ...styles.countdown,
                color: countdown.color,
                background: countdown.bg,
              }}
            >
              {countdown.text}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={styles.content}>
          <div style={styles.titleRow}>
            <button
              onClick={() => onToggleComplete?.(milestone.id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}
            >
              {milestone.completed ? (
                <CheckCircle2 size={18} color="#22c55e" />
              ) : (
                <Circle size={18} color="#ccc" />
              )}
            </button>
            <span
              onClick={() => onEditMilestone?.(milestone.id)}
              style={{
                ...styles.title,
                ...(milestone.completed ? styles.titleDone : {}),
                cursor: onEditMilestone ? 'pointer' : 'default',
              }}
            >
              {milestone.title}
            </span>
            {isOverdue && <AlertTriangle size={16} color="#dc2626" />}
          </div>

          {milestone.description && (
            <div style={styles.description}>{milestone.description}</div>
          )}

          <div style={styles.metaRow}>
            {/* Reminders */}
            {milestone.reminders && milestone.reminders.length > 0 && (
              <div style={styles.metaItem}>
                <Bell size={12} />
                {t('milestones.reminders', { count: milestone.reminders.length })}
              </div>
            )}

            {/* Involved users */}
            {milestone.involvedUsers && milestone.involvedUsers.length > 0 && (
              <div style={styles.metaItem}>
                <Users size={12} />
                {milestone.involvedUsers.map((userId) => (
                  <span key={userId} style={styles.userChip}>
                    {userId.slice(-4)}
                  </span>
                ))}
              </div>
            )}

            {/* Linked tasks */}
            {linkedTasks.length > 0 && (
              <div style={styles.metaItem}>
                <Link2 size={12} />
                {t('milestones.linkedTasksCount', { count: linkedTasks.length })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <Calendar size={16} />
          {t('milestones.title')} ({milestones.length})
        </div>
        {onAddMilestone && (
          <button onClick={onAddMilestone} style={styles.addBtn}>
            <Plus size={14} /> {t('milestones.add')}
          </button>
        )}
      </div>

      {/* Content */}
      {milestones.length === 0 ? (
        <div style={styles.emptyState}>
          <Calendar size={32} color="#ccc" style={{ marginBottom: '8px' }} />
          <p style={{ margin: 0 }}>{t('milestones.noMilestones')}</p>
          {onAddMilestone && (
            <button
              onClick={onAddMilestone}
              style={{ ...styles.addBtn, margin: '12px auto 0', background: '#666' }}
            >
              <Plus size={14} /> {t('milestones.addFirst')}
            </button>
          )}
        </div>
      ) : (
        <div style={styles.list}>
          {/* Upcoming */}
          {upcoming.length > 0 && (
            <>
              <div style={{ padding: '8px 16px', fontSize: '11px', color: '#888', fontWeight: 600, background: '#f9fafb' }}>
                {t('milestones.upcoming')}
              </div>
              {upcoming.map(renderMilestone)}
            </>
          )}

          {/* Past/Completed */}
          {past.length > 0 && (
            <>
              <div style={{ padding: '8px 16px', fontSize: '11px', color: '#888', fontWeight: 600, background: '#f9fafb' }}>
                {t('milestones.pastCompleted')}
              </div>
              {past.map(renderMilestone)}
            </>
          )}
        </div>
      )}
    </div>
  );
}
